import { useRef, useState, useMemo } from "react";
import { useVirtualizer } from "@tanstack/react-virtual";
import { SearchBox } from "../common/SearchBox";
import { ActionButton } from "../common/Buttons";
import exportCSV from "../../../utils/exportCSV";
import TableLabel from "../common/TableLabel";

const CombinedGroupedStockSubTable = ({
  tableData,
  loading,
  totals,
  columns = [],
  searchFields = ["isbnno", "prodname"],
  allowModel = true,
  selectedRow,
}) => {
  const [search, setSearch] = useState("");
  const scrollRef = useRef(null);

  const [sortConfig, setSortConfig] = useState({
    key: null,
    direction: null, // 'asc' | 'desc' | null
  });

  const handleSort = (columnKey) => {
    setSortConfig((prev) => {
      if (prev.key === columnKey) {
        // Cycle: asc → desc → none
        if (prev.direction === "asc")
          return { key: columnKey, direction: "desc" };
        if (prev.direction === "desc") return { key: null, direction: null };
        return { key: columnKey, direction: "asc" };
      }
      return { key: columnKey, direction: "asc" }; // first click
    });
  };

  // Filter data
  const filteredData = useMemo(() => {
    if (!search.trim()) return tableData;
    const searchLower = search.trim().toLowerCase();
    return tableData.filter((item) =>
      searchFields.some((field) =>
        item[field]?.toString().toLowerCase().includes(searchLower)
      )
    );
  }, [tableData, search, searchFields]);

  // Virtualizer
  const rowVirtualizer = useVirtualizer({
    count: filteredData?.length,
    getScrollElement: () => scrollRef.current,
    estimateSize: () => 42,
    overscan: 8,
  });

  const sortedData = useMemo(() => {
    if (!sortConfig.key || !sortConfig.direction) return filteredData;

    const sorted = [...filteredData].sort((a, b) => {
      const x = a[sortConfig.key];
      const y = b[sortConfig.key];

      if (x == null) return 1;
      if (y == null) return -1;

      if (typeof x === "number" && typeof y === "number") {
        return sortConfig.direction === "asc" ? x - y : y - x;
      }

      return sortConfig.direction === "asc"
        ? x.toString().localeCompare(y.toString())
        : y.toString().localeCompare(x.toString());
    });

    return sorted;
  }, [filteredData, sortConfig]);

  const TableCellHeader = ({
    column,
    value,
    className = "",
    isHeader = false,
  }) => {
    const isActive = sortConfig.key === column.key;
    return (
      <p
        className={`text-${column.align} overflow-hidden px-1 cursor-pointer ${className}`}
        style={{ flexBasis: column.width, flexShrink: 0 }}
        title={value?.toString()}
        onClick={() => handleSort(column.key)}
        onMouseDown={(e) => e.preventDefault()}
      >
        {!isHeader && column.format ? column.format(value) : value}
        {/* Icons */}
        {isActive &&
          (sortConfig.direction === "asc"
            ? "↑"
            : sortConfig.direction === "desc"
            ? "↓"
            : "")}
      </p>
    );
  };
  const TableCell = ({ column, value, className = "", isHeader = false }) => {
    return (
      <p
        className={`text-${column.align} ${
          allowModel ? "cursor-pointer" : ""
        } overflow-hidden px-1 ${className}`}
        style={{ flexBasis: column.width, flexShrink: 0 }}
        title={value?.toString()}
      >
        {!isHeader && column.format ? column.format(value) : value}
      </p>
    );
  };

  // csv export function
  // csv export function
  const handleCsvExport = () => {
    if (!sortedData?.length) return;
    exportCSV(sortedData, columns, "combined-Grouped-stock-sub-report");
  };

  return (
    <div className="w-full relative z-40 h-full text-sm flex flex-col overflow-hidden border rounded-[15px] shadow-custom-light">
      {selectedRow && (
        <TableLabel
          leftText={`Item :${selectedRow?.itemName}`}
          rightText={`Head name : ${selectedRow?.headName}`}
        />
      )}
      {/* Search Header */}
      <div className="w-full bg-sixth text-[#ffffff]">
        <div className="flex justify-between items-center px-4 pt-3">
          <div className="flex items-center gap-2 px-2 py-1 md:w-80">
            <SearchBox
              placeholder="Search orders..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              disabled={loading || tableData.length === 0}
            />
          </div>
          {sortedData?.length > 0 && (
            <ActionButton
              text={"Export CSV"}
              onClick={handleCsvExport}
              customStyle=""
            />
          )}
        </div>
      </div>

      <div className="flex-1 min-h-0 overflow-x-auto">
        <div className="min-w-fit h-full flex flex-col">
          {/* Table Header */}
          <div className="flex font-semibold px-3 py-2 pr-4 border-b bg-sixth text-[#ffffff] flex-shrink-0">
            {columns.map((col) => {
              return (
                <TableCellHeader
                  key={col.key}
                  column={col}
                  value={col.label}
                  isHeader={true}
                />
              );
            })}
          </div>

          {/* Virtualized Rows */}
          <div
            ref={scrollRef}
            className="h-[250px] bg-white text-black backdrop-blur-0 overflow-y-auto font-mont font-bold text-sm"
          >
            <div
              style={{
                height: `${rowVirtualizer.getTotalSize()}px`,
                position: "relative",
              }}
            >
              {loading ? (
                <div className="w-full h-36 flex justify-center items-center">
                  {/* <LoaderCircle className="animate-spin" /> */}
                  Loading...
                </div>
              ) : sortedData.length === 0 ? (
                <div className="">
                  <img
                    className="w-64 mx-auto"
                    src="/table/tablebg.jpg"
                    alt=""
                  />
                  <div className="text-center font-mont font-semibold text-gray-400">
                    No results yet.
                  </div>
                </div>
              ) : (
                rowVirtualizer.getVirtualItems().map((virtualItem) => {
                  // const item = filteredData[virtualItem.index];
                  const item = sortedData[virtualItem.index];
                  return (
                    <div
                      key={virtualItem.key}
                      className="flex font-semibold px-3 py-2 text-sm border-b border-b-[var(--table-row-border)] absolute top-0 left-0 w-full"
                      style={{
                        height: `${virtualItem.size}px`,
                        transform: `translateY(${virtualItem.start}px)`,
                      }}
                    >
                      {columns.map((col) => (
                        <TableCell
                          key={col.key}
                          column={col}
                          value={item[col.dataKey]}
                        />
                      ))}
                    </div>
                  );
                })
              )}
            </div>
          </div>

          {/* Table Footer */}
          <div className="flex font-semibold px-3 py-2 pr-3 border-t bg-sixth text-[#ffffff] flex-shrink-0">
            {columns.map((col, idx) => (
              <TableCell
                key={col.key}
                column={col}
                value={
                  idx === 0
                    ? `Total - (${filteredData?.length} items)`
                    : totals?.[col.dataKey] ?? ""
                }
              />
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default CombinedGroupedStockSubTable;
